import { useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ProductCard } from "../components/ProductCard";
import { ProductReviews } from "../components/ProductReviews";
import { useProducts, useCategories } from "../hooks/useCatalog";

const pretty = (value) => (value || "").replace(/_/g, " ");

export default function Category() {
  const { key = "all" } = useParams();
  const [search, setSearch] = useSearchParams();
  const q = search.get("q") || "";
  const sub = search.get("sub") || "";

  // Fetch all products for the category (no sub filter) so we can build the
  // subcategory tabs and filter instantly client-side (premium, no refetch).
  const params = useMemo(() => {
    const p = {};
    if (key !== "all") p.category = key;
    if (q) p.q = q;
    return p;
  }, [key, q]);

  const { data: allProducts = [], isLoading, isError } = useProducts(params);
  const { data: cats = [] } = useCategories();
  const loading = isLoading && allProducts.length === 0;
  const error = isError && allProducts.length === 0;

  const selectedCategory = cats.find((category) => category.key === key);
  const showSubTabs = key !== "all" && !q;

  const subChips = useMemo(() => {
    if (!showSubTabs) return [];
    const map = new Map();
    (selectedCategory?.subcategories || [])
      .filter((s) => s.active !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .forEach((s) => map.set(s.key, s.name));
    allProducts.forEach((p) => {
      if (p.subcategory && !map.has(p.subcategory)) map.set(p.subcategory, pretty(p.subcategory));
    });
    return [...map.entries()].map(([k, n]) => ({ key: k, name: n }));
  }, [showSubTabs, selectedCategory, allProducts]);

  const products = useMemo(
    () => (!sub || sub === "all" ? allProducts : allProducts.filter((p) => p.subcategory === sub)),
    [allProducts, sub],
  );

  const setSub = (value) => {
    const nextParams = new URLSearchParams(search);
    if (!value || value === "all") nextParams.delete("sub");
    else nextParams.set("sub", value);
    setSearch(nextParams, { replace: true });
  };

  const title = q
    ? `Results for "${q}"`
    : selectedCategory?.name || (key === "all" ? "All Products" : pretty(key));

  return (
    <main className="mx-auto max-w-7xl px-4 pb-28 pt-8 md:px-6 md:pb-12">
      <div className="mb-4">
        <p className="text-xs text-stone-500">
          <Link to="/" className="hover:underline">
            Home
          </Link>{" "}
          / <span className="capitalize text-stone-800">{title}</span>
        </p>
        <h1 className="mt-2 font-serif text-4xl capitalize tracking-tight text-stone-900">{title}</h1>
        {!loading && !error && (
          <p className="text-sm text-stone-500" data-testid="category-product-count">
            {products.length} items available
          </p>
        )}
      </div>

      {showSubTabs && subChips.length > 0 && (
        <div className="mb-5 flex gap-2 overflow-x-auto pb-1" data-testid="subcategory-tabs">
          <button
            type="button"
            onClick={() => setSub("all")}
            data-testid="subcategory-tab-all"
            className={`shrink-0 rounded-full border px-4 py-2 text-sm font-bold transition-colors ${
              !sub || sub === "all"
                ? "border-[#FF5200] bg-[#FF5200] text-white"
                : "border-stone-200 bg-white text-stone-700 hover:border-orange-300"
            }`}
          >
            All
          </button>
          {subChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => setSub(chip.key)}
              data-testid={`subcategory-tab-${chip.key}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-bold capitalize transition-colors ${
                sub === chip.key
                  ? "border-[#FF5200] bg-[#FF5200] text-white"
                  : "border-stone-200 bg-white text-stone-700 hover:border-orange-300"
              }`}
            >
              {chip.name}
            </button>
          ))}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-[220px_1fr]">
        <aside className="hidden md:block">
          <p className="mb-3 text-xs uppercase tracking-widest text-stone-500">Categories</p>
          <div className="space-y-1">
            <Link
              to="/c/all"
              data-testid="sidebar-cat-all"
              className={`block rounded-lg px-3 py-2 text-sm ${
                key === "all" ? "bg-stone-100 font-semibold text-stone-800" : "hover:bg-stone-50"
              }`}
            >
              All
            </Link>
            {cats.map((c) => (
              <Link
                key={c.key}
                to={`/c/${c.key}`}
                data-testid={`sidebar-cat-${c.key}`}
                className={`block rounded-lg px-3 py-2 text-sm ${
                  key === c.key ? "bg-stone-100 font-semibold text-stone-800" : "hover:bg-stone-50"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4" data-testid="category-loading">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((placeholder) => (
                <div key={placeholder} className="h-64 animate-pulse rounded-2xl bg-stone-100" />
              ))}
            </div>
          ) : error ? (
            <div className="border border-red-200 bg-red-50 p-6 text-sm text-red-800" data-testid="category-error">
              We could not load these products. Please try again.
            </div>
          ) : products.length === 0 ? (
            <div
              className="grid place-items-center border border-dashed border-stone-300 bg-white p-16 text-center"
              data-testid="category-empty"
            >
              <p className="font-serif text-2xl">No products found</p>
              <p className="mt-1 text-sm text-stone-500">Try a different category or search.</p>
              <Link
                to="/categories"
                className="mt-5 min-h-11 bg-orange-600 px-4 py-3 text-sm font-semibold text-white"
                data-testid="category-browse-link"
              >
                Browse categories
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
          {q && products.length === 1 && <ProductReviews productId={products[0].id} />}
        </div>
      </div>
    </main>
  );
}
